import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useProjectSettings } from 'frontastic/hooks';

export const RECAPTCHA_LOAD_TIMEOUT = 8000;
export const RECAPTCHA_LOAD_POLL_INTERVAL = 250;
export const RECAPTCHA_RENDER_TIMEOUT = 5000;

type RecaptchaWindow = Window & {
  grecaptcha?: { render?: unknown };
  recaptchaOptions?: { useRecaptchaNet?: boolean };
};

interface Props {
  setIsReCaptchaVerified: (isRecaptchaVerified: boolean) => void;
  setReCaptchaError: (isRecaptchaError: boolean) => void;
  setReCaptchaLoadError?: (isRecaptchaLoadError: boolean) => void;
}
const ReCaptchaUI = ({ setIsReCaptchaVerified, setReCaptchaError, setReCaptchaLoadError }: Props) => {
  const { data: config, error: settingsError, mutate: retrySettings } = useProjectSettings();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [scriptAttempt, setScriptAttempt] = useState(0);
  const [isFrameHidden, setIsFrameHidden] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const showLoadError = hasLoadError || isFrameHidden;

  const recaptchaDisabled = config?.recaptchaEnabled !== undefined && !config?.recaptchaEnabled;
  const recaptchaUnavailable = config !== undefined && !config?.recaptchaKey && !settingsError;
  const skipRecaptcha = recaptchaDisabled || recaptchaUnavailable;

  useEffect(() => {
    if (skipRecaptcha) {
      setIsReCaptchaVerified(true);
      setReCaptchaError(false);
    }
  }, [skipRecaptcha, setIsReCaptchaVerified, setReCaptchaError]);

  useEffect(() => {
    setReCaptchaLoadError?.(showLoadError);
  }, [showLoadError, setReCaptchaLoadError]);

  // Set errors when the settings or script never load (ad blockers, restricted networks)
  // The library can miss the end of the script load, so re-render once render is available to draw the widget
  // Restarts when the site key arrives, so slow settings don't eat into the script's time
  useEffect(() => {
    if (skipRecaptcha || isLoaded) return;
    const timer = setTimeout(() => setHasLoadError(true), RECAPTCHA_LOAD_TIMEOUT);
    const poll = setInterval(() => {
      if (typeof (window as RecaptchaWindow).grecaptcha?.render === 'function') {
        setIsLoaded(true);
        setHasLoadError(false);
      }
    }, RECAPTCHA_LOAD_POLL_INTERVAL);
    return () => {
      clearTimeout(timer);
      clearInterval(poll);
    };
  }, [skipRecaptcha, isLoaded, scriptAttempt, config?.recaptchaKey]);

  useEffect(() => {
    if (skipRecaptcha || !isLoaded) return;
    let poll: ReturnType<typeof setInterval> | undefined;
    const checkFrame = () => {
      const frame = containerRef.current?.querySelector('iframe');
      setIsFrameHidden(!frame || frame.offsetHeight === 0);
    };
    const timer = setTimeout(() => {
      checkFrame();
      poll = setInterval(checkFrame, RECAPTCHA_LOAD_POLL_INTERVAL);
    }, RECAPTCHA_RENDER_TIMEOUT);
    return () => {
      clearTimeout(timer);
      clearInterval(poll);
    };
  }, [skipRecaptcha, isLoaded, scriptAttempt]);

  if (skipRecaptcha) {
    return <></>;
  }

  const handleChange = (res: string | null) => {
    if (res) {
      setIsReCaptchaVerified(true);
      setReCaptchaError(false);
      setHasLoadError(false);
    }
  };

  // The library remembers a failed script URL until a full reload, so retry on Google's alternate host
  const handleScriptLoad = (state?: { errored?: boolean }) => {
    if (!state?.errored) return;

    if (scriptAttempt > 0) {
      setHasLoadError(true);
      return;
    }

    const recaptchaWindow = window as RecaptchaWindow;
    recaptchaWindow.recaptchaOptions = { ...recaptchaWindow.recaptchaOptions, useRecaptchaNet: true };
    setScriptAttempt(1);
  };

  // Remounting the widget draws it again, and refetches the settings if they never arrived
  const handleRetry = () => {
    setHasLoadError(false);
    setIsFrameHidden(false);
    setIsLoaded(false);
    if (!config?.recaptchaKey) retrySettings?.();
    setScriptAttempt((attempt) => attempt + 1);
  };

  return (
    <div>
      <div ref={containerRef} className="recaptcha-container mt-4">
        {config?.recaptchaKey && (
          <>
            <ReCAPTCHA
              key={scriptAttempt}
              onChange={handleChange}
              onErrored={() => setHasLoadError(true)}
              asyncScriptOnLoad={handleScriptLoad}
              sitekey={config?.recaptchaKey}
            />
          </>
        )}
      </div>
      {showLoadError && (
        <p className="mt-1 text-xs font-semibold text-[#DA0F0F]">
          reCAPTCHA could not be loaded. Please turn off ad or tracker blockers for this site, or try another browser.{' '}
          <button type="button" className="underline" onClick={handleRetry}>
            Try again
          </button>
        </p>
      )}
    </div>
  );
};

export default ReCaptchaUI;
